import type {
  CardGridPlan,
  EmbedPlanState,
  InsightsFailure,
  InsightsQueryResult,
  PilledAdRow,
} from "src/pages/ad-studio/insights/performance/types";
import {
  cubeLoad,
  useInsightsQuery,
} from "src/pages/ad-studio/insights/performance/use-insights-query";
import { useInsightsMeta } from "src/pages/ad-studio/insights/performance/use-insights-meta";
import {
  cardGridPlanSchema,
  type EmbedAttrs,
  toEmbedPlanState,
  toPlanResult,
  withRank,
} from "src/pages/ad-studio/insights/performance/utils/embeds";
import { buildMetricDistributions } from "src/pages/ad-studio/insights/performance/utils/metric-distribution";
import { adCardsCubeQuery } from "src/pages/ad-studio/insights/performance/utils/query";
import { mapQueryResult } from "src/pages/ad-studio/insights/performance/utils/query-result";
import { useAverages } from "src/pages/ad-studio/insights/performance/use-averages";
import type {
  CardItem,
  EmptyText,
  PagingState,
} from "src/pages/ad-studio/insights/performance/view/types";
import {
  toPagingState,
  toPilledAdCards,
} from "src/pages/ad-studio/insights/performance/view/utils";

type CardGrid = {
  cards: readonly CardItem<PilledAdRow>[];
  paging: PagingState;
  empty: EmptyText;
};

export function useCardGridEmbedPlan(attrs: EmbedAttrs): {
  state: EmbedPlanState<CardGridPlan>;
} {
  const meta = useInsightsMeta();
  return {
    state: toEmbedPlanState({
      meta,
      toPlan: (loaded) =>
        toPlanResult(cardGridPlanSchema(loaded).safeParse(attrs)),
    }),
  };
}

export function useAdCardGridEmbed({ plan }: { plan: CardGridPlan }): {
  state: { grid: InsightsQueryResult<CardGrid, InsightsFailure> };
} {
  const { query } = plan;
  const { view } = plan.list;
  const listQuery = {
    ...query,
    metrics: plan.metrics.map((column) => column.key),
    page: { limit: plan.pageSize, offset: 0 },
  };
  const cube = withRank({
    cube: adCardsCubeQuery({ query: listQuery, view }),
    rank: plan.rank,
  });
  const rows = useInsightsQuery(cubeLoad({ cube, wire: view.wire }));
  const averages = useAverages({
    list: cube.query,
    view,
    metrics: plan.metrics,
    scope: query.scope,
  });
  return {
    state: {
      grid: mapQueryResult(rows, (page) => ({
        cards: toPilledAdCards({
          rows: page.rows,
          view,
          measures: {
            metrics: plan.metrics,
            distributions: buildMetricDistributions({
              rows: page.rows,
              metrics: plan.metrics.map((column) => column.key),
              averages,
            }),
          },
        }),
        paging: toPagingState({
          page: 0,
          total: page.total,
          rowsPerPage: plan.pageSize,
          noun: "ad",
        }),
        empty: { title: "No ads found", message: plan.emptyMessage },
      })),
    },
  };
}
